import {
  CumulativeCounters,
  InboundAudioFields,
  InboundVideoFields,
  OutboundAudioFields,
  OutboundVideoFields,
  PCContext,
  StatsGraph,
} from 'utils/webrtc/stats/types';
import { selectedCandidatePair } from 'utils/webrtc/stats/selectedCandidatePair';

type RawReport = {
  id: string;
  type: string;
  kind?: 'audio' | 'video';
  mid?: string;
  rid?: string;
  ssrc?: number;
  packetsSent?: number;
  packetsReceived?: number;
  packetsLost?: number;
  bytesSent?: number;
  bytesReceived?: number;
  jitter?: number;
  roundTripTime?: number;
  framesPerSecond?: number;
  frameWidth?: number;
  frameHeight?: number;
  freezeCount?: number;
  totalFreezesDuration?: number;
  qualityLimitationReason?: 'none' | 'cpu' | 'bandwidth' | 'other';
  targetBitrate?: number;
  framesDropped?: number;
  framesDecoded?: number;
  concealedSamples?: number;
  totalSamplesReceived?: number;
  silentConcealedSamples?: number;
  concealmentEvents?: number;
  fecPacketsReceived?: number;
  jitterBufferDelay?: number;
  jitterBufferEmittedCount?: number;
  codecId?: string;
  localId?: string;
  mimeType?: string;
  timestamp?: number;
};

const SAFARI_MICROSECOND_THRESHOLD = 1e15; // ms-magnitudes are ~1.7e12; µs are ~1.7e15

const toMs = (timestamp: number | undefined): number => {
  if (timestamp === undefined) {
    return Date.now();
  }
  return timestamp > SAFARI_MICROSECOND_THRESHOLD ? Math.round(timestamp / 1000) : timestamp;
};

const trackKey = (pcKey: string, mid: string | undefined, rid: string | undefined): string =>
  `${pcKey}::${mid ?? '?'}::${rid ?? 'default'}`;

// Undefined when no per-track delta is measurable: first tick, or the first
// time this track is observed (e.g. a participant joining mid-session).
const bitrateFromBytes = (
  currentBytes: number,
  previousBytes: number | undefined,
  currentTimestampMs: number,
  previousGraph: StatsGraph | null
): number | undefined => {
  if (previousBytes === undefined) {
    return undefined;
  }
  if (previousGraph === null || previousGraph.capturedAtMs <= 0) {
    return undefined;
  }
  const tickIntervalSeconds = Math.max(0, (currentTimestampMs - previousGraph.capturedAtMs) / 1000);
  if (tickIntervalSeconds <= 0) {
    return undefined;
  }
  const bytesDelta = Math.max(0, currentBytes - previousBytes);
  return (bytesDelta * 8) / tickIntervalSeconds;
};

// Per-tick mean delay (ms) = delta(jitterBufferDelay seconds) / delta(jitterBufferEmittedCount).
// Undefined on first observation, unsupported browsers, a zero emitted-count
// delta (DTX silence), or a regressing cumulative (counter reset).
const deriveAverageJitterBufferDelayMs = (
  currentDelay: number | undefined,
  currentCount: number | undefined,
  previous: CumulativeCounters | undefined
): number | undefined => {
  if (
    currentDelay === undefined ||
    currentCount === undefined ||
    previous?.jitterBufferDelay === undefined ||
    previous?.jitterBufferEmittedCount === undefined
  ) {
    return undefined;
  }
  const deltaDelay = currentDelay - previous.jitterBufferDelay;
  const deltaCount = currentCount - previous.jitterBufferEmittedCount;
  if (deltaCount <= 0 || deltaDelay < 0) {
    return undefined;
  }
  return (deltaDelay / deltaCount) * 1000;
};

export function extractStatsGraph(
  report: RTCStatsReport,
  previousGraph: StatsGraph | null,
  pcContext: PCContext
): StatsGraph {
  const byId = new Map<string, RawReport>();
  const codecsById = new Map<string, string>(); // codecId -> mimeType
  const remoteInboundByLocalId = new Map<string, RawReport>();
  const remoteOutboundByLocalId = new Map<string, RawReport>();
  let timestamp: number | undefined;

  report.forEach((raw: RawReport) => {
    byId.set(raw.id, raw);
    if (raw.type === 'codec' && raw.id) {
      if (raw.mimeType) {
        codecsById.set(raw.id, raw.mimeType);
      }
    }
    if (raw.type === 'remote-inbound-rtp' && raw.localId) {
      remoteInboundByLocalId.set(raw.localId, raw);
    }
    if (raw.type === 'remote-outbound-rtp' && raw.localId) {
      remoteOutboundByLocalId.set(raw.localId, raw);
    }
    if (raw.timestamp !== undefined && timestamp === undefined) {
      timestamp = raw.timestamp;
    }
  });

  const transportRoundTripTime = selectedCandidatePair(report)?.currentRoundTripTime ?? null;

  // Cumulative counters live in a separate map (`cumulativeByTrackKey`) so the
  // typed field shapes only carry per-tick deltas.
  const outboundAudio = new Map<string, OutboundAudioFields>();
  const outboundVideo = new Map<string, OutboundVideoFields>();
  const inboundAudio = new Map<string, InboundAudioFields>();
  const inboundVideo = new Map<string, InboundVideoFields>();
  const cumulativeByTrackKey = new Map<string, CumulativeCounters>();

  const prevCumulative =
    previousGraph?.cumulativeByTrackKey ?? new Map<string, CumulativeCounters>();

  const deltaOf = (key: string, field: keyof CumulativeCounters, current: number): number => {
    const prev = prevCumulative.get(key)?.[field];
    if (prev === undefined) {
      return current;
    } // first tick: emit cumulative as the "delta"
    return Math.max(0, current - prev);
  };

  const optionalDeltaOf = (
    key: string,
    field: keyof CumulativeCounters,
    current: number | undefined
  ): number | undefined => (current === undefined ? undefined : deltaOf(key, field, current));

  const bitrateForTrack = (
    key: string,
    bytesField: 'bytesSent' | 'bytesReceived',
    currentBytes: number,
    rawTimestamp: number | undefined
  ): number | undefined =>
    bitrateFromBytes(
      currentBytes,
      prevCumulative.get(key)?.[bytesField],
      toMs(rawTimestamp),
      previousGraph
    );

  byId.forEach((raw) => {
    if (raw.type === 'outbound-rtp') {
      const key = trackKey(pcContext.pcKey, raw.mid, raw.rid);
      const remote = remoteInboundByLocalId.get(raw.id);
      const codec = raw.codecId ? codecsById.get(raw.codecId) : undefined;

      const cumulativePacketsSent = raw.packetsSent ?? 0;
      const cumulativeBytesSent = raw.bytesSent ?? 0;
      const cumulativeRemotePacketsLost = Math.max(0, remote?.packetsLost ?? 0);
      cumulativeByTrackKey.set(key, {
        ...(cumulativeByTrackKey.get(key) ?? {}),
        packetsSent: cumulativePacketsSent,
        bytesSent: cumulativeBytesSent,
        remotePacketsLost: cumulativeRemotePacketsLost,
      });
      const deltaSent = deltaOf(key, 'packetsSent', cumulativePacketsSent);
      const deltaRemotePacketsLost = deltaOf(key, 'remotePacketsLost', cumulativeRemotePacketsLost);

      if (raw.kind === 'audio') {
        outboundAudio.set(key, {
          packetsSent: deltaSent,
          packetsLost: deltaRemotePacketsLost,
          jitter: remote?.jitter ?? Number.NaN,
          roundTripTime: remote?.roundTripTime,
        });
      } else if (raw.kind === 'video') {
        const bitrate = bitrateForTrack(key, 'bytesSent', cumulativeBytesSent, raw.timestamp);
        outboundVideo.set(key, {
          packetsSent: deltaSent,
          packetsLost: deltaRemotePacketsLost,
          jitter: remote?.jitter ?? Number.NaN,
          roundTripTime: remote?.roundTripTime,
          framesPerSecond: raw.framesPerSecond,
          frameWidth: raw.frameWidth,
          frameHeight: raw.frameHeight,
          bitrate,
          codec,
          qualityLimitationReason: raw.qualityLimitationReason,
          targetBitrate: raw.targetBitrate,
        });
      }
    } else if (raw.type === 'inbound-rtp') {
      const key = trackKey(pcContext.pcKey, raw.mid, raw.rid);
      const codec = raw.codecId ? codecsById.get(raw.codecId) : undefined;
      // RTT for inbound tracks lives on the paired remote-outbound-rtp report
      // (RTCP SR/DLRR), not on inbound-rtp itself — RTCInboundRtpStreamStats
      // has no roundTripTime field per W3C webrtc-stats.
      const remoteOutbound = remoteOutboundByLocalId.get(raw.id);

      const cumulativePacketsReceived = raw.packetsReceived ?? 0;
      const cumulativePacketsLost = Math.max(0, raw.packetsLost ?? 0);
      const cumulativeBytesReceived = raw.bytesReceived ?? 0;
      const cumulativeConcealedSamples = raw.concealedSamples ?? 0;
      const cumulativeTotalSamplesReceived = raw.totalSamplesReceived ?? 0;
      const cumulativeSilentConcealedSamples = raw.silentConcealedSamples ?? 0;
      const cumulativeConcealmentEvents = raw.concealmentEvents ?? 0;
      const cumulativeJitterBufferDelay = raw.jitterBufferDelay;
      const cumulativeJitterBufferEmittedCount = raw.jitterBufferEmittedCount;
      cumulativeByTrackKey.set(key, {
        ...(cumulativeByTrackKey.get(key) ?? {}),
        packetsReceived: cumulativePacketsReceived,
        packetsLost: cumulativePacketsLost,
        bytesReceived: cumulativeBytesReceived,
        concealedSamples: cumulativeConcealedSamples,
        totalSamplesReceived: cumulativeTotalSamplesReceived,
        silentConcealedSamples: cumulativeSilentConcealedSamples,
        concealmentEvents: cumulativeConcealmentEvents,
        jitterBufferDelay: cumulativeJitterBufferDelay,
        jitterBufferEmittedCount: cumulativeJitterBufferEmittedCount,
        framesDropped: raw.framesDropped,
        framesDecoded: raw.framesDecoded,
        freezeCount: raw.freezeCount,
        totalFreezesDuration: raw.totalFreezesDuration,
      });

      const averageJitterBufferDelayMs = deriveAverageJitterBufferDelayMs(
        cumulativeJitterBufferDelay,
        cumulativeJitterBufferEmittedCount,
        prevCumulative.get(key)
      );

      if (raw.kind === 'audio') {
        const bitrate = bitrateForTrack(
          key,
          'bytesReceived',
          cumulativeBytesReceived,
          raw.timestamp
        );
        inboundAudio.set(key, {
          packetsLost: deltaOf(key, 'packetsLost', cumulativePacketsLost),
          packetsReceived: deltaOf(key, 'packetsReceived', cumulativePacketsReceived),
          jitter: raw.jitter ?? Number.NaN,
          roundTripTime: remoteOutbound?.roundTripTime,
          fecPacketsReceived: raw.fecPacketsReceived,
          bitrate,
          averageJitterBufferDelayMs,
          concealment: {
            concealedSamples: deltaOf(key, 'concealedSamples', cumulativeConcealedSamples),
            totalSamplesReceived: deltaOf(
              key,
              'totalSamplesReceived',
              cumulativeTotalSamplesReceived
            ),
            silentConcealedSamples: deltaOf(
              key,
              'silentConcealedSamples',
              cumulativeSilentConcealedSamples
            ),
            concealmentEvents: deltaOf(key, 'concealmentEvents', cumulativeConcealmentEvents),
          },
        });
      } else if (raw.kind === 'video') {
        const bitrate = bitrateForTrack(
          key,
          'bytesReceived',
          cumulativeBytesReceived,
          raw.timestamp
        );
        inboundVideo.set(key, {
          packetsLost: deltaOf(key, 'packetsLost', cumulativePacketsLost),
          packetsReceived: deltaOf(key, 'packetsReceived', cumulativePacketsReceived),
          jitter: raw.jitter ?? Number.NaN,
          roundTripTime: remoteOutbound?.roundTripTime,
          framesPerSecond: raw.framesPerSecond,
          frameWidth: raw.frameWidth,
          frameHeight: raw.frameHeight,
          freezeCount: optionalDeltaOf(key, 'freezeCount', raw.freezeCount),
          totalFreezesDuration: optionalDeltaOf(
            key,
            'totalFreezesDuration',
            raw.totalFreezesDuration
          ),
          framesDropped: optionalDeltaOf(key, 'framesDropped', raw.framesDropped),
          framesDecoded: optionalDeltaOf(key, 'framesDecoded', raw.framesDecoded),
          bitrate,
          codec,
          averageJitterBufferDelayMs,
        });
      }
    }
  });

  return {
    pcKey: pcContext.pcKey,
    inboundAudio,
    inboundVideo,
    outboundAudio,
    outboundVideo,
    cumulativeByTrackKey,
    capturedAtMs: toMs(timestamp),
    transportRoundTripTime,
  };
}
